import { Box, Stack } from '@mui/material';
import UnsignedChip from 'src/components/UnsignedChip';
import { formatCount } from 'src/utils/formatNumber.util';

const ONE_LINE_SX = {
  display: 'block',
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const SUBTEXT_SX = { display: 'block', fontSize: '0.62rem', color: '#9CA3AF' };

// The name and its hash share a cell, as on the app's files list, each cut to one line.
const renderFileName = row => (
  <>
    <Stack direction="row" gap={0.75} alignItems="center" sx={{ minWidth: 0 }}>
      <Box component="span" title={row.fileName} sx={ONE_LINE_SX}>
        {row.fileName || '--'}
      </Box>
      {row.signerCount === 0 && <UnsignedChip />}
    </Stack>
    <Box component="span" title={row.sha256} sx={{ ...ONE_LINE_SX, ...SUBTEXT_SX }}>
      {row.sha256 || '--'}
    </Box>
  </>
);

// The first signer stands for the rest, with a count of them beneath.
const renderSigners = row => {
  if (!row.signerCount) {
    return '--';
  }

  return (
    <>
      <Box component="span" title={row.signer || undefined} sx={ONE_LINE_SX}>
        {row.signer || 'Unnamed signer'}
      </Box>
      {row.signerCount > 1 && (
        <Box component="span" sx={SUBTEXT_SX}>
          {`+${formatCount(row.signerCount - 1)} more ${
            row.signerCount === 2 ? 'signer' : 'signers'
          }`}
        </Box>
      )}
    </>
  );
};

const renderPublisher = row => (
  <>
    {row.publisherName || '--'}
    {row.issuerName && (
      <Box component="span" sx={{ fontSize: '0.62rem', color: '#9CA3AF', ml: 0.5 }}>
        ({row.issuerName})
      </Box>
    )}
  </>
);

// The detail sections of a file set. A descriptor carries the route segment under the set, the
// array its response wraps the rows in, and the columns, so every caller that names a set draws
// the same table from the same endpoint.
export const FILES_SECTION = {
  key: 'files',
  label: 'Files',
  section: 'files',
  itemsKey: 'results',
  emptyMessage: 'This file set has no files.',
  columns: [
    { key: 'fileName', label: 'File Name', width: '26%', render: renderFileName },
    { key: 'productName', label: 'Product Name', width: '15%' },
    { key: 'fileVersion', label: 'Version', width: '10%' },
    { key: 'filePath', label: 'Full File Path', width: '31%' },
    { key: 'signers', label: 'Signers', width: '18%', render: renderSigners },
  ],
};

export const PUBLISHERS_SECTION = {
  key: 'publishers',
  label: 'Publishers',
  section: 'codeSigners',
  itemsKey: 'codeSigners',
  emptyMessage: 'Nothing here is signed.',
  columns: [
    { key: 'publisherName', label: 'Publisher', width: '34%', render: renderPublisher },
    { key: 'validFrom', label: 'Valid From', width: '18%', date: true },
    { key: 'validTo', label: 'Valid To', width: '18%', date: true },
    { key: 'tbsHash', label: 'TBS Hash', width: '30%' },
  ],
};

export const OUTSIDE_PATH_RULES_SECTION = {
  key: 'outsidePath',
  label: 'Outside Path Rules',
  section: 'outsidePathRuleFiles',
  itemsKey: 'files',
  emptyMessage: 'Everything here installs under a standard program folder.',
  columns: [
    { key: 'filePath', label: 'File Path', width: '62%' },
    { key: 'fileName', label: 'File Name', width: '38%' },
  ],
};

export const WRITABLE_FOLDERS_SECTION = {
  key: 'writableFolders',
  label: 'User Writable Folders',
  section: 'userWritableFolders',
  itemsKey: 'userWriteableFolders',
  emptyMessage: 'This application writes nothing to a user-writable folder.',
  columns: [{ key: 'path', label: 'Path', width: '100%' }],
};

// The detail sections carry their total as `count`, the paged file list as `totalCount`.
export const sectionNextPageParam = itemsKey => (lastPage, pages) => {
  const total = lastPage.count ?? lastPage.totalCount ?? 0;
  const loaded = pages.reduce((acc, page) => acc + (page[itemsKey]?.length || 0), 0);

  return loaded < total ? pages.length + 1 : undefined;
};
